import * as Sentry from '@sentry/browser';
import { LngLatBounds } from 'mapbox-gl';

interface BoundsInfo {
  sw?: { lat: number; lng: number };
  ne?: { lat: number; lng: number };
  isEmpty?: boolean;
}

interface RouteInfo {
  id?: string;
  name?: string;
  uuid?: string;
  hasPolylineArray?: boolean;
  polylineArrayLength?: number;
}

interface MapElementInfo {
  exists?: boolean;
  clientWidth?: number;
  clientHeight?: number;
}

export const formatBoundsInfo = (bounds?: LngLatBounds | null): BoundsInfo | null => {
  if (!bounds) return null;
  
  return {
    sw: bounds.getSouthWest(),
    ne: bounds.getNorthEast(),
    isEmpty: bounds.isEmpty?.() || false,
  };
};

export const formatRouteInfo = (route: any): RouteInfo => ({
  id: route?.id,
  name: route?.name,
  uuid: route?.uuid,
  hasPolylineArray: !!route?.polylineArray?.length,
  polylineArrayLength: route?.polylineArray?.length || 0,
});

// Error logging functions
export const logInvalidContainerError = (context: {
  containerWidth: number;
  containerHeight: number;
  bounds?: LngLatBounds | null;
  mapElement?: MapElementInfo;
  viewState?: any;
}) => {
  Sentry.captureException(new Error('Map recentering failed: invalid container dimensions'), {
    tags: { mapRecentering: 'failed' },
    extra: {
      reason: 'invalid_container_dimensions',
      containerWidth: context.containerWidth,
      containerHeight: context.containerHeight,
      hasBounds: !!context.bounds,
      boundsInfo: formatBoundsInfo(context.bounds),
      viewState: context.viewState,
      mapElement: context.mapElement,
    },
  });
};

export const logNullBoundsError = (context: {
  longitude?: number;
  latitude?: number;
  force?: boolean;
  viewState?: any;
  containerDimensions?: { width: number; height: number };
}) => {
  Sentry.captureException(new Error('Map coordinate centering failed: bounds is null'), {
    tags: { mapRecentering: 'failed' },
    extra: {
      reason: 'null_bounds',
      longitude: context.longitude,
      latitude: context.latitude,
      force: context.force,
      viewState: context.viewState,
      containerDimensions: context.containerDimensions,
    },
  });
};

export const logNullComputedPositionError = (context: {
  bounds?: LngLatBounds | null;
  geolocation?: any;
  panelPropertiesAndDimensions?: any;
  containerWidth: number;
  containerHeight: number;
  viewState?: any;
  fitExactBounds?: boolean;
}) => {
  Sentry.captureException(new Error('Map recentering failed: computedMapPosition is null'), {
    tags: { mapRecentering: 'failed' },
    extra: {
      reason: 'null_computed_position',
      hasBounds: !!context.bounds,
      boundsInfo: formatBoundsInfo(context.bounds),
      geolocation: context.geolocation,
      panelPropertiesAndDimensions: context.panelPropertiesAndDimensions,
      containerWidth: context.containerWidth,
      containerHeight: context.containerHeight,
      viewState: context.viewState,
      fitExactBounds: context.fitExactBounds,
    },
  });
};

export const logRouteSelectionMapRefError = (context: {
  bounds?: LngLatBounds | null;
  route?: any;
  selectedRouteCount?: number;
  triggerSource?: string;
  isInitializing?: boolean;
  viewState?: any;
}) => {
  Sentry.captureException(new Error('Route selection failed: reactMapGLRef is null when called from useRouteSelection'), {
    tags: { mapRecentering: 'failed' },
    extra: {
      reason: 'null_map_ref_route_selection',
      hasBounds: !!context.bounds,
      boundsInfo: formatBoundsInfo(context.bounds),
      routeInfo: context.route ? formatRouteInfo(context.route) : null,
      selectedRouteCount: context.selectedRouteCount,
      triggerSource: context.triggerSource,
      isInitializing: context.isInitializing,
      viewState: context.viewState,
    },
  });
};

// Success breadcrumb functions
export const logSetMapPositionAttempt = (context: {
  bounds?: LngLatBounds | null;
  fitExactBounds?: boolean;
  hasReactMapGLRef?: boolean;
  selectedRoute?: any;
  isInitializing?: boolean;
}) => {
  Sentry.addBreadcrumb({
    message: 'setMapPosition called',
    category: 'mapRecentering',
    level: 'info',
    data: {
      hasBounds: !!context.bounds,
      boundsInfo: formatBoundsInfo(context.bounds),
      fitExactBounds: context.fitExactBounds,
      hasReactMapGLRef: context.hasReactMapGLRef,
      selectedRoute: context.selectedRoute?.id,
      isInitializing: context.isInitializing,
    },
  });
};

export const logMapRecenteringSuccess = (context: {
  latitude: number;
  longitude: number;
  zoom: number;
  bounds?: LngLatBounds | null;
  containerWidth: number;
  containerHeight: number;
  fitExactBounds?: boolean;
}) => {
  Sentry.addBreadcrumb({
    message: 'Map recentering successful',
    category: 'mapRecentering',
    level: 'info',
    data: {
      latitude: context.latitude,
      longitude: context.longitude,
      zoom: context.zoom,
      hasBounds: !!context.bounds,
      containerWidth: context.containerWidth,
      containerHeight: context.containerHeight,
      fitExactBounds: context.fitExactBounds,
    },
  });
};

export const logRouteSelectionAttempt = (context: {
  route: any;
  bounds?: LngLatBounds | null;
  triggerSource: string;
}) => {
  Sentry.addBreadcrumb({
    message: 'Route selected - attempting to center map',
    category: 'mapRecentering',
    level: 'info',
    data: {
      ...formatRouteInfo(context.route),
      hasRouteBounds: !!context.bounds,
      boundsInfo: formatBoundsInfo(context.bounds),
      triggerSource: context.triggerSource,
    },
  });
};

export const logMapOperationDuringInit = (context: {
  operation: string;
  longitude?: number;
  latitude?: number;
  force?: boolean;
  bounds?: LngLatBounds | null;
  isInitializing?: boolean;
}) => {
  Sentry.addBreadcrumb({
    message: 'Map operation attempted during initialization',
    category: 'mapRecentering',
    level: 'info',
    data: {
      operation: context.operation,
      longitude: context.longitude,
      latitude: context.latitude,
      force: context.force,
      hasBounds: !!context.bounds,
      isInitializing: context.isInitializing,
    },
  });
};

export const logCoordinateCenteringSuccess = (context: {
  longitude: number;
  latitude: number;
  force?: boolean;
  wasVisible?: boolean;
  offsetCoordinates?: number[];
  panelProps?: any;
}) => {
  Sentry.addBreadcrumb({
    message: 'Map coordinate centering successful',
    category: 'mapRecentering',
    level: 'info',
    data: {
      longitude: context.longitude,
      latitude: context.latitude,
      force: context.force,
      wasVisible: context.wasVisible,
      offsetCoordinates: context.offsetCoordinates,
      panelProps: context.panelProps,
    },
  });
}; 
